import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ProblemError } from "@k60/contracts";

import { api } from "./client";

export interface Me {
  id: string;
  email: string;
  displayName: string;
}

export const ME_KEY = ["me"] as const;

const WRONG_CREDENTIALS = "E-Mail oder Passwort stimmen nicht.";
const LOGIN_UNREACHABLE = "Der Anmeldedienst ist nicht erreichbar.";

/**
 * The one place that turns a `GET /api/v1/auth/me` call into `Me | null`.
 * Used both by `useMe()`'s `queryFn` and, directly, by `useMeCache().refresh`
 * — the latter needs the same answer outside of `useQuery`, right after a
 * successful login, before anything reads the cache again.
 *
 * Two shapes have to resolve to that same `null`: the client's error
 * middleware (`api/client.ts`) throws `ProblemError` for any non-2xx
 * response in the real app, while tests stub `api.GET` directly and hand
 * back a plain `{ data, response }` pair without going through it at all.
 */
async function fetchMe(): Promise<Me | null> {
  try {
    const { data, response } = await api.GET("/api/v1/auth/me");
    if (response.status === 401) return null;
    return data ?? null;
  } catch (cause) {
    if (cause instanceof ProblemError && cause.status === 401) return null;
    throw cause;
  }
}

/**
 * Whoever holds the session cookie, or nobody. A 401 is the ordinary
 * "not signed in" answer here, not a failure — resolved as `null` and not
 * retried, so `RequireSession` sees a settled answer instead of a spinner
 * that never ends.
 */
export function useMe() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: fetchMe,
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * Rejects with a message in the form's own voice — the wrong-credentials
 * line verbatim, whatever else the server says otherwise, and a fixed German
 * line for anything that never reached the server at all (the dev proxy
 * down, a network failure — nothing the client's `ProblemError` middleware
 * ever saw, so nothing with a `.userMessage` to read). Same two shapes as
 * `useMe`: a thrown `ProblemError` in the real app, a resolved `{ response }`
 * in tests that stub `api.POST` directly.
 */
export async function login(input: { email: string; password: string }): Promise<void> {
  let outcome: { response: Response };
  try {
    outcome = await api.POST("/api/v1/auth/login", { body: input });
  } catch (cause) {
    if (cause instanceof ProblemError) {
      throw new Error(cause.status === 401 ? WRONG_CREDENTIALS : cause.userMessage);
    }
    // Not a ProblemError — the client's own errorMiddleware never ran (a
    // network failure, a CORS rejection, the dev proxy being down). Wrapped
    // the same way a ProblemError would present it, so the screen never
    // shows a raw fetch message such as "Failed to fetch".
    throw new ProblemError(
      { type: "about:blank", title: LOGIN_UNREACHABLE, status: 0, detail: LOGIN_UNREACHABLE },
      null,
    );
  }
  if (outcome.response.status === 401) throw new Error(WRONG_CREDENTIALS);
  if (!outcome.response.ok) throw new Error("Anmeldung fehlgeschlagen.");
}

export async function logout(): Promise<void> {
  await api.POST("/api/v1/auth/logout");
}

/**
 * Writes straight to the `["me"]` cache entry rather than asking React Query
 * to refetch it — `invalidateQueries`/`refetchQueries` only affect queries
 * with an *active observer*, and right after a successful login there is
 * none: the login screen sits outside `RequireSession`, so nothing is
 * subscribed to `useMe()` at that moment. Marking the query merely "stale"
 * there does nothing until something observes it again — which is exactly
 * the observer the following `navigate()` is about to create, so the first
 * render of that observer would still see whatever was cached *before* the
 * login (typically `null`, left behind by the redirect that sent the visitor
 * to `/anmelden` in the first place) and bounce straight back. Writing the
 * answer synchronously, before navigating, closes that gap.
 */
export function useMeCache() {
  const client = useQueryClient();
  return {
    /** Fetches `/me` once and writes the answer into the cache, returning it. */
    async refresh(): Promise<Me | null> {
      const me = await fetchMe();
      client.setQueryData(ME_KEY, me);
      return me;
    },
    /** Writes `null` into the cache — logging out is `refresh` without a fetch. */
    clear(): void {
      client.setQueryData(ME_KEY, null);
    },
  };
}
